/**
 * OziExplorer の校正ファイル（`.map`）の解析。
 *
 * 原典GoalViewの `map.txt`（`mapcal.ts`）とは別形式だが、競技地図の校正が
 * こちらの形式で配られることがある（渡良瀬2026の連絡会資料がこの形式だった）。
 * 出力を `MapCalibration` に揃えてあるので、`toMapPixel`/`fromMapPixel` は
 * どちらの形式から読んでもそのまま使える。
 *
 * ```
 * OziExplorer Map Data File Version 2.2
 * watarase2026-107_Renrakukai.tif
 * ...
 * Map Projection,(UTM) Universal Transverse Mercator,PolyCal,No,...
 * Point01,xy, 1729,  941,in, deg,    ,        ,N,    ,        ,W, grid, 54,  367000, 4032000,N
 * ...
 * IWH,Map Image Width/Height,10564,9241
 * ```
 *
 * 校正点は**緯度経度（deg欄）でもUTMグリッド（grid欄）でも書ける**。
 * 実データはグリッド指定だったが、両方を受ける。
 */

import type { AffineParams } from './utm.ts';
import { affineScale, fromUtm, solveAffine, toUtm } from './utm.ts';
import type { MapCalibration, CalibrationPoint } from './mapcal.ts';
import { parseMapCalibration } from './mapcal.ts';

/** 先頭行でOziExplorerの校正ファイルかどうかを判定する */
export function looksLikeOziMap(text: string): boolean {
  return /^OziExplorer\s+Map\s+Data\s+File/i.test(text.trimStart());
}

/** "  1729 " のような桁揃えされた数値欄を読む。空欄は null */
function num(field: string | undefined): number | null {
  if (field === undefined) return null;
  const t = field.trim();
  if (t.length === 0) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * 度＋分（`35,  14.1234`）または度のみの欄を10進度にする。
 * 半球（N/S・E/W）が S / W なら符号を反転する。
 */
function toDecimalDegrees(
  degField: string | undefined,
  minField: string | undefined,
  hemisphere: string | undefined,
): number | null {
  const d = num(degField);
  if (d === null) return null;
  const min = num(minField) ?? 0;
  const value = Math.abs(d) + min / 60;
  const h = (hemisphere ?? '').trim().toUpperCase();
  return h === 'S' || h === 'W' ? -value : value;
}

/**
 * OziExplorer の `.map` を解析する。
 *
 * @param text ファイル全体。座標欄はASCIIなので、`map.txt` と同じく
 *             latin1 で読み込んでも壊れない
 * @returns 校正点が3点に満たない等で変換を作れない場合も、読めた範囲を返す
 *          （`primary` が null になる）。形式自体が違う場合は null
 */
export function parseOziMap(text: string): MapCalibration | null {
  if (!looksLikeOziMap(text)) return null;

  const lines = text.split('\n').map((l) => l.replace(/[\r\n]+$/, ''));
  const ignored: string[] = [];

  // 2行目が画像ファイル名（3行目はそれを作った環境の絶対パスなので使わない）
  const image = (lines[1] ?? '').trim();

  let variation = 0;
  let imageWidth: number | null = null;
  let imageHeight: number | null = null;
  let projection = '';
  const points: CalibrationPoint[] = [];

  for (const line of lines) {
    const f = line.split(',');
    const head = (f[0] ?? '').trim();

    if (/^Magnetic Variation$/i.test(head)) {
      // `Magnetic Variation,<deg>,<min>,<E|W>`。西偏を負にする（map.txtと同じ規約）
      const v = toDecimalDegrees(f[1], f[2], f[3]);
      if (v !== null) variation = v;
      continue;
    }
    if (/^Map Projection$/i.test(head)) {
      projection = (f[1] ?? '').trim();
      continue;
    }
    if (head === 'IWH') {
      imageWidth = num(f[2]);
      imageHeight = num(f[3]);
      continue;
    }
    if (!/^Point\d+$/i.test(head)) continue;

    // Point01,xy, <x>, <y>,in, deg, <latD>,<latM>,<N|S>, <lonD>,<lonM>,<E|W>, grid, <zone>, <easting>, <northing>,<N|S>
    const px = num(f[2]);
    const py = num(f[3]);
    if (px === null || py === null) continue; // 未使用の点は座標欄が空

    const lat = toDecimalDegrees(f[6], f[7], f[8]);
    const lon = toDecimalDegrees(f[9], f[10], f[11]);
    const zone = num(f[13]);
    const easting = num(f[14]);
    const northing = num(f[15]);

    if (lat !== null && lon !== null) {
      // 緯度経度で書かれている場合
      points.push({ px, py, lat, lon, utm: toUtm(lon, lat) });
    } else if (zone !== null && easting !== null && northing !== null) {
      // UTMグリッドで書かれている場合。緯度経度は逆変換で埋める
      const ll = fromUtm({ easting, northing, zone });
      points.push({ px, py, lat: ll.lat, lon: ll.lon, utm: { easting, northing, zone } });
    } else {
      ignored.push(line.trim());
    }
  }

  // ★ 先頭3点だけでなく「3点以上あれば最初の3点」で変換を作る。OziExplorerは
  //   4隅を指定することが多く、4点目は検算に使える（呼び出し側の責務）
  const primary: AffineParams | null =
    points.length >= 3
      ? solveAffine(points.slice(0, 3).map((p) => ({ utm: p.utm, px: p.px, py: p.py })))
      : null;

  // 縮尺は宣言値ではなく校正点から実測する（Oziには縮尺欄が無いため）
  const scale = primary ? affineScale(primary).scaleX : 0;

  return {
    bmp: image,
    scale,
    // Oziは1つの投影で1枚を校正するので、ゾーンまたぎの二次変換は持たない
    zonePoint: 999999,
    variation,
    points,
    primary,
    secondary: null,
    ignored,
    imageWidth,
    imageHeight,
    projection,
  };
}

/**
 * 校正ファイルを形式（OziExplorerの`.map`／原典GoalViewの`map.txt`）を
 * 判別して読む。会場の`map.txt`はどちらの形式でもそのままコピーされる
 * （`scripts/prepare-venue.mjs`）ため、読む側で判別する必要がある。
 */
export function parseAnyMapCalibration(text: string): MapCalibration {
  return parseOziMap(text) ?? parseMapCalibration(text);
}
